import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

type CardVariant = 'public' | 'admin';

type CardProps = HTMLAttributes<HTMLDivElement> & {
  variant?: CardVariant;
  /** Wird die Card geklickt → leichter Hover-Effekt */
  interactive?: boolean;
  /** Optional: Tricolore-Akzent als Top-Border */
  tricoloreTop?: boolean;
};

/**
 * Sizilianer-Card mit kompositioneller API.
 *
 * Public-Card: warm-creme Hintergrund, gold-Border.
 * Admin-Card: weißer Hintergrund, schmaler Border.
 *
 * @example
 *   <Card variant="public" interactive>
 *     <Card.Image src="..." alt="..." />
 *     <Card.Body>
 *       <Card.Title>Pasta alla Norma</Card.Title>
 *       <Card.Subtitle>Mit Aubergine, Tomate, Ricotta Salata</Card.Subtitle>
 *       <Card.Footer>
 *         <Card.Price>14,50 €</Card.Price>
 *       </Card.Footer>
 *     </Card.Body>
 *   </Card>
 */
export function Card({
  variant = 'public',
  interactive = false,
  tricoloreTop = false,
  className,
  children,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md',
        variant === 'public' &&
          cn(
            'bg-paper-2 border border-rule',
            interactive &&
              'cursor-pointer transition-[color,background-color,border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md',
          ),
        variant === 'admin' &&
          cn(
            'bg-white border border-rule',
            interactive &&
              'cursor-pointer transition-colors hover:border-gold',
          ),
        className,
      )}
      {...props}
    >
      {tricoloreTop && (
        <div className="absolute inset-x-0 top-0 h-[3px] flex" aria-hidden="true">
          <span className="flex-1 bg-it-green" />
          <span className="flex-1 bg-gold" />
          <span className="flex-1 bg-it-red" />
        </div>
      )}
      {children}
    </div>
  );
}

/* ============================================================
   Compound-Subkomponenten
   ============================================================ */

type SlotProps = HTMLAttributes<HTMLDivElement> & { children?: ReactNode };

Card.Image = function CardImage({
  src,
  alt,
  className,
  ...props
}: { src: string; alt: string } & HTMLAttributes<HTMLImageElement>) {
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={cn('w-full aspect-[16/9] object-cover', className)}
      {...props}
    />
  );
};

Card.Body = function CardBody({ className, children, ...props }: SlotProps) {
  return (
    <div className={cn('flex flex-col gap-2 p-5', className)} {...props}>
      {children}
    </div>
  );
};

Card.Title = function CardTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn('font-serif text-xl text-ink-2 leading-tight', className)}
      {...props}
    >
      {children}
    </h3>
  );
};

Card.Subtitle = function CardSubtitle({ className, children, ...props }: SlotProps) {
  return (
    <p className={cn('font-sans text-sm text-ink-soft', className)} {...props}>
      {children}
    </p>
  );
};

Card.Footer = function CardFooter({ className, children, ...props }: SlotProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between mt-auto pt-3 border-t border-rule-soft',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

Card.Price = function CardPrice({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn('font-serif text-lg text-gold-2 font-medium', className)}
      {...props}
    >
      {children}
    </span>
  );
};
